import React, { useCallback, useState, TouchEvent, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, IconButton, AppBar, Toolbar, Typography, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { styled } from '@mui/material/styles';
import { createRoot, Root } from 'react-dom/client';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
// 인터페이스 정의
interface PrintData {
  title: string;
  ATTN: string;
  FROM: string;
  행사인원: string;
  TOURFEE: string;
  포함사항: string;
  불포함사항: string;
  쇼핑안내: string;
  비고: string;
  itinerary: PrintDay[];
  file_url: string;
  locations: string;
  places: string;
  상품명: string;
}

interface PrintDay {
  day: string;
  locations: PrintLocation[];
  meals: string[];
}

interface PrintLocation {
  place: string;
  schedule: PrintScheduleItem[];
}

interface PrintScheduleItem {
  time?: string;
  details: string[];
  isFirst?: boolean;
  flight?: string;
}

interface LocationState {
  printData: PrintData;
  itinerary: {
    내용: string;
  };
}

// 타입 정의 추가
interface RowImage {
  data: string;
  height: number;
  width: number;
  isItineraryRow: boolean;
}

// 스타일 컴포넌트
const PrintContainer = styled(Box)({
  width: '960px',
  margin: '0 auto',
  backgroundColor: 'white',
  '@media print': {
    padding: '5mm',
    paddingTop: '0mm',
    margin: '0 auto',
    width: '194mm',
    minHeight: 'auto',
    height: 'auto',
    position: 'relative',
  },
});

const PrintHeader = styled(AppBar)({
  backgroundColor: 'white',
  color: 'black',
  boxShadow: 'none',
  borderBottom: '1px solid #eee',
  '@media print': {
    display: 'none'
  }
});

const PrintPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  const [printFontSize, setPrintFontSize] = useState(12);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const rootRef = useRef<Root | null>(null);
  const tableBodyRef = useRef<HTMLElement | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  // const handlePrint = useCallback(() => {
  //   window.print();
  // }, []);

  const handleBack = () => {
    navigate(-1);
  };



  const renderDay = (day: PrintDay, dayIndex: number) => {
    const rows: React.ReactNode[] = [];
    let isFirstDayRow = true;
    let mealIndex = 0;

    day.locations.forEach((location, locIndex) => {
      const isLastLocation = locIndex === day.locations.length - 1;

      location.schedule.forEach((item, scheduleIndex) => {
        const isLastSchedule = scheduleIndex === location.schedule.length - 1;
        const isLastInDay = isLastLocation && isLastSchedule;
        const isFirstInLocation = scheduleIndex === 0;

        rows.push(
          <tr
            key={`day-${dayIndex}-loc-${locIndex}-sch-${scheduleIndex}`}
            className={isLastInDay ? 'day-separator' : ''}
            style={{ borderBottom: isLastInDay ? '1px solid #ccc !important' : 'none', marginBottom: '10px'}}>
            <td
              style={{                 
                visibility: isFirstDayRow ? 'visible' : 'hidden',
                textAlign: 'center',
              }}>
              {isFirstDayRow ? `${day.day}` : ''}
            </td>
            <td
              style={{                 
                visibility: isFirstInLocation ? 'visible' : 'hidden',
                textAlign: 'left',
              }}>
              {isFirstInLocation ? location.place : ''}
            </td>
            <td
              style={{                 
                textAlign: 'center',
              }}>
              {item.time || ''}
            </td>
            <td
              style={{                 
                textAlign: 'center',
              }}> 
              {item.flight || ''}
            </td>
            <td style={{ maxWidth: '60%' }}>
              {item.details.join(', ')}
            </td>
            <td
              style={{                 
                textAlign: 'left',
              }}>
              {mealIndex < day.meals.length ? day.meals[mealIndex] : ''}
            </td>
          </tr>
        );

        isFirstDayRow = false;
        mealIndex++;
      });
    });

    return rows;
  };

  const renderPrintContent = () => {
    if (!state?.printData || !state?.itinerary) return null;
  
    const tempDiv = document.createElement('div');
    
    // 기존 스타일 수정 로직
    let modifiedContent = state.itinerary.내용;
    modifiedContent = modifiedContent.replace(/font-size:\s*\d+px/g, `font-size: ${printFontSize}px`)
      .replace('max-width: 768px', 'max-width: 200px');
  
    // 2. 새로운 프린트용 스타일 추가
    const printStyle = `
      <style>
        @media print {
          * { font-size: ${printFontSize}px !important; }
          table { 
            width: 100% !important;
            border-collapse: collapse !important;
            display: table !important;
          }
          tr { display: table-row !important; }
          td, th { 
            display: table-cell !important;
            padding: 0px !important;
          }
        }
      </style>
    `;
  
    // 3. 수정된 내용을 tempDiv에 삽입
    tempDiv.innerHTML = printStyle + modifiedContent;
  
    // 일정표 생성 함수
    const createItineraryTable = () => {
      let currentDay = '';
      let isFirstRowOfDay = true;

      return state.printData.itinerary.map((day, dayIndex) => {
        return day.locations.map((location, locIndex) => {
          const isLastLocation = locIndex === day.locations.length - 1;

          return location.schedule.map((item, scheduleIndex) => {
            const isLastSchedule = scheduleIndex === location.schedule.length - 1;
            const isLastInDay = isLastLocation && isLastSchedule;
            const isFirstInLocation = scheduleIndex === 0;
            const isNewDay = currentDay !== day.day;
            
            if (isNewDay) {
              currentDay = day.day;
              isFirstRowOfDay = true;
            } else {
              isFirstRowOfDay = false;
            }

            return (
              <tr
                key={`day-${dayIndex}-loc-${locIndex}-sch-${scheduleIndex}`}
                style={{ 
                  borderBottom: isLastInDay ? '1px solid #000' : 'none'
                }}
              >
                <td style={{ 
                  visibility: isFirstInLocation ? 'visible' : 'hidden', 
                  textAlign: 'center',
                  paddingTop: isFirstRowOfDay ? '5px' : '0',
                  paddingBottom: isLastInDay ? '5px' : '0'
                }}>
                  {isFirstInLocation ? `${day.day}` : ''}
                </td>
                <td style={{ 
                  visibility: isFirstInLocation ? 'visible' : 'hidden', 
                  textAlign: 'left',
                  paddingTop: isFirstRowOfDay ? '5px' : '0',
                  paddingBottom: isLastInDay ? '5px' : '0'
                }}>
                  {isFirstInLocation ? location.place : ''}
                </td>
                <td style={{ 
                  textAlign: 'center',
                  paddingTop: isFirstRowOfDay ? '5px' : '0',
                  paddingBottom: isLastInDay ? '5px' : '0'
                }}>
                  {item.time || ''}
                </td>
                <td style={{ 
                  textAlign: 'center',
                  paddingTop: isFirstRowOfDay ? '5px' : '0',
                  paddingBottom: isLastInDay ? '5px' : '0'
                }}>
                  {item.flight || ''}
                </td>
                <td style={{ 
                  maxWidth: '60%',
                  paddingTop: isFirstRowOfDay ? '5px' : '0',
                  paddingBottom: isLastInDay ? '5px' : '0'
                }}>
                  {item.details.join(', ')}
                </td>
                <td style={{ 
                  textAlign: 'left',
                  paddingTop: isFirstRowOfDay ? '5px' : '0',
                  paddingBottom: isLastInDay ? '5px' : '0'
                }}>
                  {isFirstInLocation ? day.meals[locIndex] : ''}
                </td>
              </tr>
            );
          });
        });
      });
    };

    // itinerary-section 교체
    const itinerarySection = tempDiv.querySelector('.itinerary-section');
    if (itinerarySection) {
      const newTableContainer = document.createElement('div');
      newTableContainer.className = 'itinerary-table-container';
      
      const tableHtml = `
      <div style="margin: 20px">
        <table class="itinerary-table" style="width: 100%; border-collapse: collapse; font-size: ${printFontSize}px;">
          <thead>
            <tr style="border-top: 1px solid #000; border-bottom: 1px solid #000; margin-bottom: 10px; background-color: #f5f5f5;">
              <th style="text-align: center; font-weight: 400; padding: 10px 2px; width: 8%;">일자</th>
              <th style="text-align: left; font-weight: 400; padding: 10px 2px; width: 10%;">지역</th>
              <th style="text-align: center; font-weight: 400; padding: 10px 2px; width: 10%;">시간</th>
              <th style="text-align: center; font-weight: 400; padding: 10px 2px; width: 10%;">교통편</th>
              <th style="font-weight: 400; padding: 10px 2px; max-width: 60%;">주요일정</th>
              <th style="text-align: left; font-weight: 400; padding: 10px 2px; width: 15%;">식사</th>
            </tr>
          </thead>
          <tbody id="itinerary-table-body"></tbody>
        </table>
      </div>
      `;
      
      newTableContainer.innerHTML = tableHtml;
      itinerarySection.parentNode?.replaceChild(newTableContainer, itinerarySection);
    }
  
    return (
      <PrintContainer>
        <div 
          dangerouslySetInnerHTML={{ __html: tempDiv.innerHTML }} 
          ref={(el) => {
            if (el) {
              const tableBody = el.querySelector('#itinerary-table-body');
              if (tableBody && state.printData.itinerary) {
                tableBodyRef.current = tableBody as HTMLElement;
                if (!rootRef.current) {
                  rootRef.current = createRoot(tableBody);
                }
                rootRef.current.render(<>{createItineraryTable()}</>);
              }
            }
          }}
        />
      </PrintContainer>
    );
  };

  const handleViewPDF = async () => {
    setIsPdfLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 100));

      const printContent = document.querySelector('.print-content');
      if (!printContent) return;

      // html2canvas 공통 옵션 설정
      const canvasOptions = {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
        onclone: (clonedDoc: Document) => {
          const tables = clonedDoc.getElementsByTagName('table');
          Array.from(tables).forEach(table => {
            if (!table.classList.contains('itinerary-table')) {
              // 테이블의 모든 셀에서 배경색 제거
              const cells = table.getElementsByTagName('td');
              Array.from(cells).forEach(cell => {
                const computedStyle = window.getComputedStyle(cell);
                
                // 기존 테두리 스타일 유지하면서 배경색만 제거
                cell.style.backgroundColor = 'transparent';
                
                // 테두리 스타일 강화
                if (computedStyle.borderTopWidth !== '0px') {
                  cell.style.borderTop = '1px solid #000';
                }
                if (computedStyle.borderBottomWidth !== '0px') {
                  cell.style.borderBottom = '1px solid #000';
                }
                if (computedStyle.borderLeftWidth !== '0px') {
                  cell.style.borderLeft = '1px solid #ccc';
                }
                if (computedStyle.borderRightWidth !== '0px') {
                  cell.style.borderRight = '1px solid #ccc';
                }

                // 내부 요소들의 배경색도 제거
                const children = cell.getElementsByTagName('*');
                Array.from(children).forEach(child => {
                  (child as HTMLElement).style.backgroundColor = 'transparent';
                });
              });
            }
          });
        }
      };

      // 테이블 헤더 준비
      const tableHeader = document.querySelector('thead tr');
      const headerHeight = 8;
      let headerImage: string | undefined;
      
      if (tableHeader) {
        const headerCanvas = await html2canvas(tableHeader as HTMLElement, {
          ...canvasOptions,
          height: tableHeader.clientHeight,
        });
        headerImage = headerCanvas.toDataURL('image/jpeg', 1.0);
      }

      // 테이블 행들을 개별적으로 처리
      const rows = Array.from(printContent.querySelectorAll('tbody tr'));
      const rowImages: RowImage[] = await Promise.all(
        rows.map(async row => {
          const canvas = await html2canvas(row as HTMLElement, canvasOptions);
          return {
            data: canvas.toDataURL('image/jpeg', 1.0),
            height: canvas.height,
            width: canvas.width,
            isItineraryRow: row.closest('.itinerary-table') !== null
          };
        })
      );

      // PDF 설정
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // 페이지 설정
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = {
        top: 10,
        bottom: 10,
        left: 10,
        right: 10
      };
      const spacing = {
        header: 1,
        footer: 1
      };

      let currentY = margin.top;
      let currentPage = 1;
      let isFirstPage = true;
      let isTableStarted = false;  // 테이블 시작 여부 체크

      // 헤더 추가 함수
      const addHeader = () => {
        if (headerImage) {
          pdf.addImage(
            headerImage,
            'JPEG',
            margin.left,
            currentY,  // 현재 위치에 헤더 추가
            pageWidth - (margin.left + margin.right),
            headerHeight
          );
          currentY += headerHeight + spacing.header;
        }
      };

      // 행 추가
      for (const row of rowImages) {
        const rowHeight = (row.height * pageWidth) / row.width;

        // 페이지 넘김 체크
        if (currentY + rowHeight > pageHeight - (margin.bottom + spacing.footer)) {
          pdf.addPage();
          currentPage++;
          isFirstPage = false;
          currentY = margin.top;
          
          // 테이블 영역인 경우에만 새 페이지에 헤더 추가
          if (isTableStarted) {
            addHeader();
          }
        }

        // 테이블의 첫 행을 만났을 때 헤더 추가
        if (row.isItineraryRow && !isTableStarted) {
          isTableStarted = true;
          addHeader();
        }

        // 행 추가
        pdf.addImage(
          row.data,
          'JPEG',
          margin.left,
          currentY,
          pageWidth - (margin.left + margin.right),
          rowHeight
        );

        currentY += rowHeight;
      }

      const pdfOutput = pdf.output('bloburl');
      window.open(pdfOutput, '_blank');

    } catch (error) {
      console.error('PDF 생성 실패:', error);
    } finally {
      setIsPdfLoading(false);  // PDF 변환 완료
    }
  };

  // cleanup 효과
  useEffect(() => {
    return () => {
      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }
      tableBodyRef.current = null;
    };
  }, []);

  return (
    <Box>
      <PrintHeader position="static">
        <Toolbar>
          <IconButton edge="start" onClick={handleBack} color="inherit">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            인쇄 미리보기
          </Typography>
          <IconButton 
            onClick={handleViewPDF} 
            color="inherit"
            sx={{ mr: 1 }}
            disabled={isPdfLoading}
          >
            <PictureAsPdfIcon />
          </IconButton>
          {/* <IconButton onClick={handlePrint} color="inherit">
            <PrintIcon />
          </IconButton> */}
        </Toolbar>
      </PrintHeader>
      <Box 
        sx={{ 
          mt: 0.5, 
          p: 0,
          height: 'calc(100vh - 64px)',
          overflowY: 'auto',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          ...(isMobile && {
            touchAction: 'auto',
          })
        }}
      >
        <PrintContainer>
          <Box
            className="print-content"
            sx={{
              width: '960px',
              minWidth: '960px',
              ...(isMobile && {
                minHeight: '100%',
                transform: 'scale(1)',

              })
            }}
          >
            {renderPrintContent()}
          </Box>
        </PrintContainer>
      </Box>
      {isPdfLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2, fontSize: '16px' }}>
            PDF 변환 중입니다...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PrintPage;
